import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Clock3,
  EyeOff,
  FileText,
  FileUp,
  FolderOpen,
  Lock,
  Search,
  ShieldCheck,
} from 'lucide-react';
import LaborStandaloneLayout from '../../components/labor/LaborStandaloneLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  getLaborCaseByTracking,
  getLaborCaseByTrackingToken,
  submitLaborCompletion,
} from '../../features/labor-intake/laborIntakeService';
import { useLaborPortalLanguage } from '../../features/labor-intake/LaborPortalLanguageContext';

const statusLabels = {
  ar: {
    new: 'جديد',
    under_review: 'قيد المراجعة',
    missing_required: 'نواقص مطلوبة',
    waiting_client: 'بانتظار العميل',
    waiting_documents: 'بانتظار استكمال النواقص',
    documents_completed: 'تم استكمال النواقص',
    missing_review: 'بانتظار مراجعة النواقص المستكملة',
    completed: 'مكتمل',
    complete: 'مكتمل',
    preliminarily_accepted: 'مقبول مبدئياً',
    incomplete: 'غير مكتمل',
    rejected: 'مرفوض',
    assigned_lawyer: 'محال للمحامي',
    archived: 'مؤرشف',
  },
  en: {
    new: 'New',
    under_review: 'Under review',
    missing_required: 'Missing requirements',
    waiting_client: 'Waiting for client',
    waiting_documents: 'Waiting for missing items',
    documents_completed: 'Missing items completed',
    missing_review: 'Waiting for completed items review',
    completed: 'Completed',
    complete: 'Completed',
    preliminarily_accepted: 'Preliminarily accepted',
    incomplete: 'Incomplete',
    rejected: 'Rejected',
    assigned_lawyer: 'Assigned to lawyer',
    archived: 'Archived',
  },
  ur: {
    new: 'نیا',
    under_review: 'زیرِ جائزہ',
    missing_required: 'مطلوبہ معلومات نامکمل',
    waiting_client: 'کلائنٹ کے جواب کا انتظار',
    waiting_documents: 'درکار معلومات یا دستاویزات کا انتظار',
    documents_completed: 'درکار معلومات جمع کر دی گئی ہیں',
    missing_review: 'جمع شدہ معلومات کے جائزے کا انتظار',
    completed: 'مکمل',
    complete: 'مکمل',
    preliminarily_accepted: 'ابتدائی طور پر منظور',
    incomplete: 'نامکمل',
    rejected: 'مسترد',
    assigned_lawyer: 'وکیل کو تفویض',
    archived: 'محفوظ شدہ',
  },
  bn: {
    new: 'নতুন',
    under_review: 'পর্যালোচনাধীন',
    missing_required: 'প্রয়োজনীয় তথ্য অসম্পূর্ণ',
    waiting_client: 'ক্লায়েন্টের অপেক্ষায়',
    waiting_documents: 'অসম্পূর্ণ তথ্য বা নথির অপেক্ষায়',
    documents_completed: 'প্রয়োজনীয় তথ্য জমা হয়েছে',
    missing_review: 'জমা দেওয়া তথ্য পর্যালোচনার অপেক্ষায়',
    completed: 'সম্পন্ন',
    complete: 'সম্পন্ন',
    preliminarily_accepted: 'প্রাথমিকভাবে গৃহীত',
    incomplete: 'অসম্পূর্ণ',
    rejected: 'প্রত্যাখ্যাত',
    assigned_lawyer: 'আইনজীবীর কাছে অর্পিত',
    archived: 'সংরক্ষিত',
  },
  id: {
    new: 'Baru',
    under_review: 'Sedang ditinjau',
    missing_required: 'Persyaratan belum lengkap',
    waiting_client: 'Menunggu klien',
    waiting_documents: 'Menunggu kelengkapan',
    documents_completed: 'Kelengkapan telah dikirim',
    missing_review: 'Menunggu peninjauan kelengkapan',
    completed: 'Selesai',
    complete: 'Selesai',
    preliminarily_accepted: 'Diterima sementara',
    incomplete: 'Belum lengkap',
    rejected: 'Ditolak',
    assigned_lawyer: 'Ditugaskan kepada pengacara',
    archived: 'Diarsipkan',
  },
  ml: {
    new: 'പുതിയത്',
    under_review: 'പരിശോധനയിൽ',
    missing_required: 'ആവശ്യമായ വിവരങ്ങൾ അപൂർണ്ണമാണ്',
    waiting_client: 'ക്ലയന്റിന്റെ മറുപടി കാത്തിരിക്കുന്നു',
    waiting_documents: 'കുറവുള്ള വിവരങ്ങളോ രേഖകളോ കാത്തിരിക്കുന്നു',
    documents_completed: 'ആവശ്യമായ വിവരങ്ങൾ സമർപ്പിച്ചു',
    missing_review: 'സമർപ്പിച്ച വിവരങ്ങളുടെ പരിശോധന കാത്തിരിക്കുന്നു',
    completed: 'പൂർത്തിയായി',
    complete: 'പൂർത്തിയായി',
    preliminarily_accepted: 'പ്രാഥമികമായി അംഗീകരിച്ചു',
    incomplete: 'അപൂർണ്ണം',
    rejected: 'നിരസിച്ചു',
    assigned_lawyer: 'അഭിഭാഷകനു കൈമാറി',
    archived: 'ആർക്കൈവ് ചെയ്തു',
  },
  tl: {
    new: 'Bago',
    under_review: 'Sinusuri',
    missing_required: 'May kulang na kinakailangan',
    waiting_client: 'Naghihintay sa kliyente',
    waiting_documents: 'Naghihintay ng mga kulang na impormasyon o dokumento',
    documents_completed: 'Naisumite na ang mga kinakailangang impormasyon',
    missing_review: 'Naghihintay ng pagsusuri sa mga isinumiteng kulang',
    completed: 'Kumpleto',
    complete: 'Kumpleto',
    preliminarily_accepted: 'Paunang tinanggap',
    incomplete: 'Hindi kumpleto',
    rejected: 'Tinanggihan',
    assigned_lawyer: 'Itinalaga sa abogado',
    archived: 'Naka-archive',
  },
};

const steps = [
  { id: 'client', label: 'بيانات العميل', icon: ShieldCheck },
  { id: 'company', label: 'بيانات المنشأة', icon: FolderOpen },
  { id: 'employment', label: 'العلاقة والأجر', icon: Lock },
  { id: 'facts', label: 'الوقائع', icon: FileText },
  { id: 'claims', label: 'المطالبات', icon: AlertTriangle },
  { id: 'attachments', label: 'المرفقات', icon: FileUp },
  { id: 'review', label: 'المراجعة النهائية', icon: CheckCircle2 },
];

const progressSteps = ['تم استلام الطلب', 'قيد المراجعة', 'طلب استكمال النواقص', 'تم استكمال النواقص', 'المراجعة النهائية', 'مكتمل'];
const progressIndexByStatus = {
  new: 0,
  under_review: 1,
  missing_required: 2,
  waiting_documents: 2,
  waiting_client: 2,
  documents_completed: 3,
  missing_review: 3,
  preliminarily_accepted: 4,
  assigned_lawyer: 4,
  complete: 5,
  completed: 5,
  archived: 5,
};

const formatDate = (value, locale = 'ar-SA') =>
  value ? new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value)) : '-';
const dash = (value) => (value === undefined || value === null || value === '' ? '-' : value);
const fullName = (client = {}) => client.fullName || [client.firstName, client.fatherName, client.grandfatherName, client.familyName].filter(Boolean).join(' ') || '-';
const companyName = (company = {}) => company.tradeName || company.legalName || '-';
const money = (value) => (value || value === 0 ? `${new Intl.NumberFormat('ar-SA', { maximumFractionDigits: 2 }).format(Number(value) || 0)} ريال` : '-');
const listValue = (items) => (Array.isArray(items) && items.length ? items.join('، ') : '-');
const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const attachmentTypes = [
  ['identity', 'الهوية الوطنية'],
  ['resident_identity', 'هوية مقيم'],
  ['national_address', 'العنوان الوطني'],
  ['contract', 'العقد أو ما يثبت علاقة العمل'],
  ['bank_statement', 'كشف حساب بنكي'],
  ['payroll', 'مسيرات أجور'],
  ['gosi_wages_certificate', 'شهادة المدد والأجور من التأمينات الاجتماعية'],
  ['termination_notice', 'إشعار إنهاء أو فصل'],
  ['release', 'مخالصة'],
  ['service_certificate', 'شهادة الخدمة'],
  ['clearance', 'إخلاء طرف'],
  ['messages', 'رسائل واتساب أو بريد إلكتروني'],
  ['other_attachments', 'مستندات داعمة أخرى'],
];

const getStatusLabel = (caseItem, language) => {
  const locale = statusLabels[language] ? language : 'en';
  const status = caseItem?.status || caseItem?.adminData?.status;
  if (statusLabels[locale][status]) return statusLabels[locale][status];
  const label = caseItem?.statusLabel;
  return label && language === 'ar' && !/^[a-z_]+$/i.test(label) ? label : dash(statusLabels[locale][status] || status);
};

const inferRequiredSections = (request) => {
  if (Array.isArray(request?.requiredSections) && request.requiredSections.length) {
    return {
      client: request.requiredSections.includes('client'),
      company: request.requiredSections.includes('company'),
      employment: request.requiredSections.includes('employment'),
      facts: request.requiredSections.includes('facts'),
      claims: request.requiredSections.includes('claims'),
      attachments: request.requiredSections.includes('attachments'),
    };
  }
  const text = `${request?.teamNotes || ''} ${request?.incompleteReasons || ''} ${(request?.requiredDocuments || []).join(' ')}`;
  const hasRequest = Boolean(request);
  return {
    client: hasRequest && /عميل|هوية|إقامة|جوال|ميلاد|عنوان|مدينة/i.test(text),
    company: hasRequest && /منشأة|شركة|سجل|ترخيص|صاحب العمل|موارد/i.test(text),
    employment: hasRequest && /عقد|علاقة|أجر|الأجر|قوى|تأمينات|بدل|دوام/i.test(text),
    facts: hasRequest && /واقعة|وقائع|سبب|إنهاء|إشعار|فصل|استقالة|مخالصة|قصة/i.test(text),
    claims: hasRequest && /مطالب|أجور|أجر|إجاز|عمولة|تعويض|مستحق|مبلغ/i.test(text),
    attachments: hasRequest && (/مستند|مرفق|إرفاق|هوية|عقد|كشف|شهادة|ملف|صورة|pdf/i.test(text) || request?.requiredDocuments?.length),
  };
};

const LaborIntakeTrackingPage = () => {
  const { language, isArabic, text, languageConfig } = useLaborPortalLanguage();
  const { trackingToken: routeTrackingToken } = useParams();
  const [searchParams] = useSearchParams();
  const initialReference = searchParams.get('ref') || '';
  const initialToken = routeTrackingToken || searchParams.get('token') || '';
  const initialMobile = searchParams.get('mobile') || '';
  const [referenceNumber, setReferenceNumber] = useState(initialReference);
  const [trackingToken, setTrackingToken] = useState(initialToken);
  const [clientMobile, setClientMobile] = useState(initialMobile);
  const [trackedCase, setTrackedCase] = useState(null);
  const [formData, setFormData] = useState(null);
  const [activeStep, setActiveStep] = useState('client');
  const [showMissingPanel, setShowMissingPanel] = useState(true);
  const [attachmentDraft, setAttachmentDraft] = useState({ typeKey: '', displayName: '', files: [] });
  const [completionAttachments, setCompletionAttachments] = useState([]);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [searchAttempted, setSearchAttempted] = useState(Boolean(routeTrackingToken || (initialReference && (initialToken || initialMobile))));
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadCase = async () => {
    if (!routeTrackingToken && (!referenceNumber || (!trackingToken && !clientMobile))) return;
    setLoading(true);
    setError('');
    try {
      const result = routeTrackingToken
        ? await getLaborCaseByTrackingToken(routeTrackingToken)
        : await getLaborCaseByTracking({ referenceNumber, trackingToken, clientMobile });
      setTrackedCase(result);
      setFormData(clone(result));
      if (result?.trackingToken) setTrackingToken(result.trackingToken);
    } catch (loadError) {
      setError(loadError.message || 'تعذر تحميل الطلب.');
      setTrackedCase(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (routeTrackingToken || (initialReference && (initialToken || initialMobile))) loadCase();
  }, [routeTrackingToken]);

  const completionRequest = trackedCase?.clientCompletionRequest;
  const completionSubmitted = completionRequest?.status === 'submitted' || ['documents_completed', 'missing_review'].includes(trackedCase?.status);
  const isCompletionMode = Boolean(completionRequest) && !completionSubmitted;
  const requiredSections = useMemo(() => inferRequiredSections(completionRequest), [completionRequest]);
  const currentProgressIndex = progressIndexByStatus[trackedCase?.status] ?? 0;
  const attachments = useMemo(() => [
    ...(formData?.attachments?.additional || []),
    ...(formData?.attachments?.initial || []),
  ], [formData]);

  const updateSection = (section, key, value) => {
    setFormData((current) => ({
      ...current,
      [section]: {
        ...(current?.[section] || {}),
        [key]: value,
      },
    }));
  };

  const updateNested = (section, parent, key, value) => {
    setFormData((current) => ({
      ...current,
      [section]: {
        ...(current?.[section] || {}),
        [parent]: {
          ...(current?.[section]?.[parent] || {}),
          [key]: value,
        },
      },
    }));
  };

  const submitCompletion = async () => {
    if (!trackedCase || !formData) return;
    setSubmitting(true);
    setError('');
    try {
      const updates = {
        client: formData.client || {},
        company: formData.company || {},
        contract: formData.contract || {},
        employment: formData.employment || {},
        compensation: {
          ...(formData.compensation || {}),
          claims: Array.isArray(formData.compensation?.claims)
            ? formData.compensation.claims
            : String(formData.compensation?.claims || '').split(',').map((item) => item.trim()).filter(Boolean),
        },
        benefits: formData.benefits || {},
        notices: formData.notices || {},
        narrative: formData.narrative || {},
        summary: formData.summary || {},
      };
      const updated = await submitLaborCompletion({
        caseId: trackedCase.id,
        trackingToken: trackedCase.trackingToken || trackingToken,
        updates,
        attachments: completionAttachments,
      });
      setTrackedCase(updated);
      setFormData(clone(updated));
      setUploadedCount(completionAttachments.reduce((count, item) => count + (item.files?.length || 0), 0));
      setCompletionAttachments([]);
      setAttachmentDraft({ typeKey: '', displayName: '', files: [] });
    } catch (submitError) {
      setError(submitError.message || 'تعذر إرسال الاستكمال.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    setSearchAttempted(true);
    await loadCase();
  };

  const currentStepIndex = steps.findIndex((step) => step.id === activeStep);
  const goToStep = (offset) => {
    const nextIndex = Math.min(Math.max(currentStepIndex + offset, 0), steps.length - 1);
    setActiveStep(steps[nextIndex].id);
  };

  return (
    <LaborStandaloneLayout footerNote={text('قريباً… يتم إطلاق منصة وازن تدريجياً.', 'Coming soon... Wazin platform is being launched gradually.')}>
      <main className="px-4 pb-20 pt-12">
        <div className="mx-auto max-w-6xl space-y-6">
          <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-2xl font-black text-[#1F3B64]">{trackedCase ? `متابعة الطلب ${trackedCase.referenceNumber}` : 'متابعة الطلب'}</CardTitle>
                  <CardDescription className="mt-2 text-sm leading-7 text-slate-600">
                    {trackedCase ? 'هذه الصفحة مخصصة لمتابعة الطلب واستكمال النواقص المطلوبة فقط عند وجودها.' : 'أدخل بيانات المتابعة أو استخدم الرابط المباشر الخاص بطلبكم.'}
                  </CardDescription>
                </div>
                {trackedCase ? <span className="rounded-full bg-[#A27849]/12 px-4 py-2 text-sm font-bold text-[#A27849]">{getStatusLabel(trackedCase, language)}</span> : null}
              </div>
            </CardHeader>
            <CardContent>
              {!routeTrackingToken ? (
                <form onSubmit={handleSearch} className="grid gap-4 md:grid-cols-[1fr_1fr_1fr_auto]">
                  <Field label="رقم الطلب" value={referenceNumber} onChange={setReferenceNumber} placeholder="WZN-LB-2026-000101" />
                  <Field label="رقم الجوال" value={clientMobile} onChange={setClientMobile} placeholder="05xxxxxxxx" />
                  <Field label="رمز المتابعة" value={trackingToken} onChange={setTrackingToken} placeholder="WZ104" />
                  <Button type="submit" className="mt-7 h-12 rounded-xl bg-[#1F3B64] px-6 text-white hover:bg-[#264b7a]">
                    عرض الطلب <Search className="h-4 w-4" />
                  </Button>
                </form>
              ) : (
                <div className="grid gap-3 text-sm md:grid-cols-3">
                  <Info label="رقم الطلب" value={trackedCase?.referenceNumber || '-'} />
                  <Info label="رقم الجوال" value={trackedCase?.client?.mobile || '-'} />
                  <Info label="رمز المتابعة" value={trackedCase?.trackingToken || routeTrackingToken} />
                </div>
              )}
              {error ? <p className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{error}</p> : null}
            </CardContent>
          </Card>

          {!trackedCase && searchAttempted && !loading ? (
            <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-black text-[#1F3B64]">تعذر العثور على الطلب</h2>
                <p className="mt-4 text-sm leading-7 text-slate-600">يرجى التأكد من رقم الطلب ورقم الجوال أو رمز المتابعة.</p>
                <Button asChild variant="outline" className="mt-6 rounded-xl border-[#C8A870]/30 px-6 py-6 text-[#1F3B64]"><Link to="/labor-intake">العودة إلى بوابة الطلبات</Link></Button>
              </CardContent>
            </Card>
          ) : null}

          {trackedCase && formData ? (
            <>
              <Progress currentIndex={currentProgressIndex} status={trackedCase.status} />

              {completionRequest ? (
                <div className="sticky top-3 z-20">
                  <Card className="rounded-[24px] border-red-200 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                    <CardHeader className="py-4">
                      <div className="flex items-center justify-between gap-3">
                        <CardTitle className="flex items-center gap-2 text-lg font-black text-[#1F3B64]"><AlertTriangle className="h-5 w-5 text-red-600" /> أسباب عدم الاكتمال</CardTitle>
                        <button type="button" onClick={() => setShowMissingPanel((value) => !value)} className="inline-flex items-center gap-2 rounded-xl bg-[#f8f6f1] px-3 py-2 text-xs font-bold text-[#1F3B64]">
                          <EyeOff className="h-4 w-4" /> {showMissingPanel ? 'إخفاء' : 'إظهار'}
                        </button>
                      </div>
                    </CardHeader>
                    {showMissingPanel ? <MissingPanel request={completionRequest} submitted={completionSubmitted} requiredSections={requiredSections} /> : null}
                  </Card>
                </div>
              ) : null}

              <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                <CardContent className="p-4">
                  <div className="grid gap-2 md:grid-cols-7">
                    {steps.map(({ id, label, icon: Icon }) => {
                      const editable = isCompletionMode && (requiredSections[id] || id === 'review');
                      const active = activeStep === id;
                      return (
                        <button key={id} type="button" onClick={() => setActiveStep(id)} className={`rounded-2xl border px-3 py-4 text-center text-sm font-bold transition ${active ? 'border-[#1F3B64] bg-[#f8f6f1] text-[#1F3B64]' : 'border-[#C8A870]/15 bg-white text-slate-600 hover:bg-[#f8f6f1]'}`}>
                          <Icon className={`mx-auto mb-2 h-5 w-5 ${editable ? 'text-red-600' : 'text-[#5d7b68]'}`} />
                          {label}
                          <span className={`mt-2 block text-xs ${editable ? 'text-red-600' : 'text-slate-500'}`}>{editable ? 'يتطلب استكمالاً' : 'تم التأكيد'}</span>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <StepContent
                step={activeStep}
                data={formData}
                updateSection={updateSection}
                updateNested={updateNested}
                editable={isCompletionMode && requiredSections[activeStep]}
                requiredSections={requiredSections}
                attachmentDraft={attachmentDraft}
                setAttachmentDraft={setAttachmentDraft}
                completionAttachments={completionAttachments}
                setCompletionAttachments={setCompletionAttachments}
                attachments={[...(formData.attachments?.additional || []), ...(formData.attachments?.initial || [])]}
              />

              <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
                  <Button type="button" variant="outline" disabled={currentStepIndex === 0} onClick={() => goToStep(-1)} className="rounded-xl border-[#C8A870]/35 px-6 py-5 text-[#1F3B64] disabled:opacity-40">
                    السابق
                  </Button>
                  <div className="text-sm font-bold text-slate-500">
                    الخطوة {currentStepIndex + 1} من {steps.length}
                  </div>
                  {currentStepIndex < steps.length - 1 ? (
                    <Button type="button" onClick={() => goToStep(1)} className="rounded-xl bg-[#1F3B64] px-6 py-5 text-white hover:bg-[#264b7a]">
                      التالي
                    </Button>
                  ) : (
                    <Button disabled={!isCompletionMode || submitting} onClick={submitCompletion} className="rounded-xl bg-[#1F3B64] px-7 py-5 text-white hover:bg-[#264b7a] disabled:opacity-50">
                      {submitting ? 'جارٍ الإرسال...' : 'إرسال الاستكمال'}
                      <CheckCircle2 className="h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>

              {uploadedCount > 0 ? (
                <p className="rounded-2xl bg-[#A27849]/10 px-4 py-3 text-sm font-bold text-[#A27849]">تم تسجيل {uploadedCount} مستند/مستندات إضافية.</p>
              ) : null}

              <Timeline items={trackedCase.timeline || []} />
            </>
          ) : null}
        </div>
      </main>
    </LaborStandaloneLayout>
  );
};

const Field = ({ label, value, onChange, disabled = false, textarea = false, ...props }) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-[#1F3B64]">{label}</label>
    {textarea ? (
      <textarea {...props} value={value || ''} disabled={disabled} onChange={(event) => onChange(event.target.value)} className="min-h-[120px] w-full rounded-xl border border-[#C8A870]/20 bg-[#fcfbf8] px-4 py-3 text-sm leading-7 outline-none focus:ring-2 focus:ring-[#A27849]/30 disabled:bg-slate-50 disabled:text-slate-500" />
    ) : (
      <Input {...props} value={value || ''} disabled={disabled} onChange={(event) => onChange(event.target.value)} className="h-12 rounded-xl border-[#C8A870]/20 bg-[#fcfbf8] focus-visible:ring-[#A27849] disabled:bg-slate-50 disabled:text-slate-500" />
    )}
  </div>
);

const Info = ({ label, value }) => (
  <div className="rounded-2xl border border-[#C8A870]/15 bg-[#f8f6f1] px-4 py-3">
    <p className="text-xs font-bold text-slate-500">{label}</p>
    <p className="mt-1 font-black text-[#1F3B64]">{dash(value)}</p>
  </div>
);

const Progress = ({ currentIndex, status }) => (
  <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
    <CardContent className="grid gap-3 p-5 md:grid-cols-6">
      {progressSteps.map((label, index) => {
        const done = index < currentIndex;
        const active = index === currentIndex;
        const missing = index === 2 && ['waiting_documents', 'missing_required'].includes(status);
        const Icon = done ? CheckCircle2 : active && missing ? AlertTriangle : active ? Clock3 : Circle;
        return (
          <div key={label} className={`rounded-2xl border p-4 text-sm ${active ? 'border-[#A27849]/35 bg-[#A27849]/8' : 'border-[#C8A870]/15 bg-[#f8f6f1]'}`}>
            <Icon className={`mb-2 h-5 w-5 ${done ? 'text-[#5d7b68]' : missing ? 'text-red-600' : 'text-[#1F3B64]'}`} />
            <p className={`font-bold ${active ? 'text-[#1F3B64]' : 'text-slate-600'}`}>{label}</p>
          </div>
        );
      })}
    </CardContent>
  </Card>
);

const MissingPanel = ({ request, submitted, requiredSections }) => (
  <CardContent className="grid gap-4 p-5 pt-0 md:grid-cols-[1.2fr_1fr_1fr]">
    <div className="rounded-[20px] border border-[#C8A870]/15 bg-[#f8f6f1] p-4 text-sm leading-8 text-slate-700">
      <p className="font-bold text-[#1F3B64]">ملاحظات الفريق</p>
      <p className="mt-2">{dash(request.teamNotes)}</p>
      <p className="mt-4 text-xs text-slate-500">طُلب بواسطة {request.requestedBy || 'فريق وازن'} بتاريخ {formatDate(request.requestedAt)}.</p>
    </div>
    <div className="rounded-[20px] border border-[#C8A870]/15 bg-[#f8f6f1] p-4 text-sm leading-8 text-slate-700">
      <p className="font-bold text-[#1F3B64]">أسباب النقص</p>
      <p className="mt-2">{dash(request.incompleteReasons)}</p>
      <span className={`mt-4 inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${submitted ? 'bg-[#eef5f0] text-[#4f6f5c]' : 'bg-red-50 text-red-700'}`}>{submitted ? 'تم إرسال الاستكمال' : 'بانتظار الاستكمال'}</span>
    </div>
    <div className="rounded-[20px] border border-[#C8A870]/15 bg-[#f8f6f1] p-4 text-sm leading-8 text-slate-700">
      <p className="font-bold text-[#1F3B64]">المستندات والأقسام المطلوبة</p>
      {!!request.requiredDocuments?.length ? (
        <ul className="mt-2 list-inside list-disc space-y-1">{request.requiredDocuments.map((documentName) => <li key={documentName}>{documentName}</li>)}</ul>
      ) : <p className="mt-2">-</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {steps.filter((step) => requiredSections[step.id]).map((step) => <span key={step.id} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-red-700">{step.label}</span>)}
      </div>
    </div>
  </CardContent>
);

const StepCard = ({ title, editable, children }) => (
  <Card className={`rounded-[28px] bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)] ${editable ? 'border-red-200' : 'border-[#C8A870]/20'}`}>
    <CardHeader>
      <div className="flex items-center justify-between gap-3">
        <CardTitle className="text-xl font-black text-[#1F3B64]">{title}</CardTitle>
        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold ${editable ? 'bg-red-50 text-red-700' : 'bg-[#eef5f0] text-[#4f6f5c]'}`}>
          {editable ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          {editable ? 'يمكن الاستكمال' : 'تم التأكيد'}
        </span>
      </div>
      {!editable ? <CardDescription className="text-sm text-slate-500">هذا القسم مقفل للقراءة فقط ولا يتطلب تعديلًا حالياً.</CardDescription> : null}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const StepContent = ({ step, data, updateSection, updateNested, editable, attachmentDraft, setAttachmentDraft, completionAttachments, setCompletionAttachments, attachments }) => {
  if (step === 'client') return (
    <StepCard title="بيانات العميل" editable={editable}>
      <div className="grid gap-4 md:grid-cols-2">
        <Field disabled={!editable} label="الاسم الكامل" value={fullName(data.client)} onChange={(value) => updateSection('client', 'fullName', value)} />
        <Field disabled={!editable} label="رقم الهوية" value={data.client?.nationalId} onChange={(value) => updateSection('client', 'nationalId', value)} />
        <Field disabled={!editable} label="تاريخ الميلاد" type="date" value={data.client?.birthDate} onChange={(value) => updateSection('client', 'birthDate', value)} />
        <Field disabled={!editable} label="رقم الجوال" value={data.client?.mobile} onChange={(value) => updateSection('client', 'mobile', value)} />
        <Field disabled={!editable} label="البريد الإلكتروني" value={data.client?.email} onChange={(value) => updateSection('client', 'email', value)} />
        <Field disabled={!editable} label="المدينة" value={data.client?.city} onChange={(value) => updateSection('client', 'city', value)} />
      </div>
    </StepCard>
  );

  if (step === 'company') return (
    <StepCard title="بيانات المنشأة" editable={editable}>
      <div className="grid gap-4 md:grid-cols-2">
        <Field disabled={!editable} label="اسم المنشأة" value={companyName(data.company)} onChange={(value) => updateSection('company', 'tradeName', value)} />
        <Field disabled={!editable} label="نوع المنشأة" value={data.company?.establishmentType} onChange={(value) => updateSection('company', 'establishmentType', value)} />
        <Field disabled={!editable} label="المقر الرئيسي" value={data.company?.headOffice} onChange={(value) => updateSection('company', 'headOffice', value)} />
        <Field disabled={!editable} label="المسؤول المباشر" value={data.company?.directSupervisor} onChange={(value) => updateSection('company', 'directSupervisor', value)} />
        <Field disabled={!editable} label="مسؤول الموارد البشرية" value={data.company?.hrContact} onChange={(value) => updateSection('company', 'hrContact', value)} />
        <Field disabled={!editable} label="القطاع" value={data.company?.sector} onChange={(value) => updateSection('company', 'sector', value)} />
      </div>
    </StepCard>
  );

  if (step === 'employment') return (
    <StepCard title="العلاقة والأجر" editable={editable}>
      <div className="grid gap-4 md:grid-cols-2">
        <Field disabled={!editable} label="نوع العقد" value={data.contract?.contractType} onChange={(value) => updateSection('contract', 'contractType', value)} />
        <Field disabled={!editable} label="تاريخ مباشرة العمل" type="date" value={data.contract?.startDate} onChange={(value) => updateSection('contract', 'startDate', value)} />
        <Field disabled={!editable} label="الأجر الأساسي" value={data.compensation?.basicSalary} onChange={(value) => updateSection('compensation', 'basicSalary', value)} />
        <Field disabled={!editable} label="الأجر الشامل" value={data.compensation?.totalSalary} onChange={(value) => updateSection('compensation', 'totalSalary', value)} />
        <Field disabled={!editable} label="طريقة استلام الأجر" value={data.compensation?.paymentMethod || data.contract?.salaryReceiptMethod} onChange={(value) => updateSection('compensation', 'paymentMethod', value)} />
        <Field disabled={!editable} label="تاريخ صرف الأجر" type="date" value={data.compensation?.payDate} onChange={(value) => updateSection('compensation', 'payDate', value)} />
      </div>
    </StepCard>
  );

  if (step === 'facts') return (
    <StepCard title="الوقائع" editable={editable}>
      <div className="grid gap-4 md:grid-cols-2">
        <Field disabled={!editable} label="حالة العلاقة" value={data.contract?.currentStatus} onChange={(value) => updateSection('contract', 'currentStatus', value)} />
        <Field disabled={!editable} label="سبب انتهاء العلاقة" value={data.contract?.endReason} onChange={(value) => updateSection('contract', 'endReason', value)} />
        <Field disabled={!editable} label="تاريخ آخر يوم عمل" type="date" value={data.contract?.lastDay} onChange={(value) => updateSection('contract', 'lastDay', value)} />
        <Field disabled={!editable} label="تاريخ الإشعار" type="date" value={data.contract?.noticeDate} onChange={(value) => updateSection('contract', 'noticeDate', value)} />
        <div className="md:col-span-2"><Field textarea disabled={!editable} label="سرد الوقائع" value={data.narrative?.fullStory || data.contract?.endStory} onChange={(value) => updateSection('narrative', 'fullStory', value)} /></div>
      </div>
    </StepCard>
  );

  if (step === 'claims') return (
    <StepCard title="المطالبات" editable={editable}>
      <div className="grid gap-4 md:grid-cols-2">
        <Field disabled={!editable} label="المطالبات المحددة" value={Array.isArray(data.compensation?.claims) ? data.compensation.claims.join('، ') : data.compensation?.claims} onChange={(value) => updateSection('compensation', 'claims', value.split(/[،,]/).map((item) => item.trim()).filter(Boolean))} />
        <Field disabled={!editable} label="إجمالي الأجور المتأخرة" value={money(data.compensation?.financialTotals?.delayedWages)} onChange={() => {}} />
        <Field disabled={!editable} label="إجمالي بدل الإجازات" value={money(data.compensation?.financialTotals?.leaveCompensation)} onChange={() => {}} />
        <Field disabled={!editable} label="إجمالي العمولات" value={money(data.compensation?.financialTotals?.commissions)} onChange={() => {}} />
        <Field disabled={!editable} label="نوع الضرر" value={data.compensation?.damageClaim?.damageType} onChange={(value) => updateNested('compensation', 'damageClaim', 'damageType', value)} />
        <Field disabled={!editable} label="المبلغ المطالب به للتعويض عن الأضرار" value={data.compensation?.damageClaim?.claimedAmount} onChange={(value) => updateNested('compensation', 'damageClaim', 'claimedAmount', value)} />
        <div className="md:col-span-2"><Field textarea disabled={!editable} label="وصف الضرر أو تفاصيل المطالبات" value={data.compensation?.damageClaim?.description} onChange={(value) => updateNested('compensation', 'damageClaim', 'description', value)} /></div>
      </div>
    </StepCard>
  );

  if (step === 'attachments') return (
    <StepCard title="المرفقات" editable={editable}>
      <div className="space-y-4">
        {editable ? (
          <div className="space-y-4 rounded-[24px] border border-dashed border-[#C8A870]/35 bg-[#f8f6f1] p-5">
            <p className="mb-3 text-base font-black text-[#1F3B64]">إرفاق مستندات إضافية</p>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-bold text-[#1F3B64]">نوع المرفق</label>
                <select value={attachmentDraft.typeKey} onChange={(event) => {
                  const selected = attachmentTypes.find(([key]) => key === event.target.value);
                  setAttachmentDraft((value) => ({ ...value, typeKey: event.target.value, typeLabel: selected?.[1] || '' }));
                }} className="h-12 w-full rounded-xl border border-[#C8A870]/20 bg-white px-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[#A27849]/30">
                  <option value="">اختر نوع المرفق</option>
                  {attachmentTypes.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </div>
              <Field label="اسم المرفق" value={attachmentDraft.displayName} onChange={(value) => setAttachmentDraft((current) => ({ ...current, displayName: value }))} placeholder="مثال: عقد العمل، كشف حساب مارس" />
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-bold text-[#1F3B64]">رفع الملف</label>
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.zip" onChange={(event) => setAttachmentDraft((current) => ({ ...current, files: Array.from(event.target.files || []) }))} className="block w-full rounded-2xl border border-[#C8A870]/20 bg-white px-4 py-3 text-sm text-slate-700 file:ml-4 file:rounded-xl file:border-0 file:bg-[#1F3B64] file:px-4 file:py-2 file:text-white" />
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={!attachmentDraft.typeKey || !attachmentDraft.files?.length}
              onClick={() => {
                setCompletionAttachments((current) => [...current, { ...attachmentDraft, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }]);
                setAttachmentDraft({ typeKey: '', typeLabel: '', displayName: '', files: [] });
              }}
              className="rounded-xl border-[#C8A870]/35 px-5 py-5 text-[#1F3B64] disabled:opacity-40"
            >
              إضافة مرفق آخر
            </Button>
          </div>
        ) : null}
        {completionAttachments.length ? (
          <div className="space-y-3">
            <p className="text-sm font-black text-[#1F3B64]">مرفقات سيتم إرسالها مع الاستكمال</p>
            {completionAttachments.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#C8A870]/15 bg-white px-4 py-3 text-sm">
                <div>
                  <p className="font-bold text-[#1F3B64]">{item.displayName || item.typeLabel}</p>
                  <p className="mt-1 text-slate-500">{item.typeLabel} | {(item.files || []).map((file) => file.name).join('، ')}</p>
                </div>
                {editable ? <button type="button" onClick={() => setCompletionAttachments((current) => current.filter((attachment) => attachment.id !== item.id))} className="text-sm font-bold text-red-600">حذف قبل الإرسال</button> : null}
              </div>
            ))}
          </div>
        ) : null}
        <div className="grid gap-3">
          {attachments.map((file) => (
            <div key={file.id} className="flex items-center justify-between rounded-2xl border border-[#C8A870]/15 bg-[#f8f6f1] px-4 py-3 text-sm">
              <div><p className="font-bold text-[#1F3B64]">{file.displayName || file.fileName || file.name || '-'}</p><p className="mt-1 text-slate-500">{file.typeLabel || file.type || 'مرفق'}</p></div>
              <span className="text-xs text-slate-400">{formatDate(file.uploadedAt)}</span>
            </div>
          ))}
          {!attachments.length ? <div className="rounded-2xl border border-[#C8A870]/15 bg-[#f8f6f1] px-4 py-5 text-sm text-slate-500">لا توجد مستندات مرفوعة حتى الآن.</div> : null}
        </div>
      </div>
    </StepCard>
  );

  return (
    <StepCard title="المراجعة النهائية" editable>
      <div className="space-y-4 text-sm leading-8 text-slate-700">
        <p>راجع الأقسام المطلوبة ثم اضغط “إرسال الاستكمال”. سيتم تحديث نفس الطلب الأصلي وإرسال الاستكمال لفريق وازن للمراجعة.</p>
        <div className="grid gap-3 md:grid-cols-3">
          <Info label="العميل" value={fullName(data.client)} />
          <Info label="المنشأة" value={companyName(data.company)} />
          <Info label="المطالبات" value={listValue(data.compensation?.claims)} />
        </div>
      </div>
    </StepCard>
  );
};

const Timeline = ({ items }) => (
  <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
    <CardHeader><CardTitle className="text-xl font-black text-[#1F3B64]">تحديثات الحالة</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      {items.map((item) => (
        <div key={item.id} className="rounded-[24px] border border-[#C8A870]/15 bg-[#f8f6f1] p-5">
          <p className="font-bold text-[#1F3B64]">{item.label}</p>
          <p className="mt-2 text-sm leading-7 text-slate-600">{item.description}</p>
          <p className="mt-3 text-xs text-slate-400">{formatDate(item.createdAt)}</p>
        </div>
      ))}
    </CardContent>
  </Card>
);

export default LaborIntakeTrackingPage;
